import React from 'react'
import { useSelector } from 'react-redux'
import { Base64 } from 'js-base64'
import { Grid, Typography, Divider, Stack, Checkbox, FormControlLabel } from '@mui/material'
import { useTheme } from '@mui/styles'
import { groupFinishedGoodsByMaterial } from 'services/finishedGoods'
import MaterialsSummaryTable from 'components/generic/table/MaterialsSummaryTable'
import SignatureDateLine from 'components/generic/printable/components/SignatureDateLine'
import {
  convertShippingInfoToCityStateZip,
  countryCodeToName,
  dateFormatter,
  formatAddress,
} from 'utils/data-manipulation/valueFormatter'
import { aggregateFGsAndMaterials } from 'components/containers/outbound-tickets/utils'
import { formatNumber } from 'utils/helpers'
import { Caption, GridSpacer } from '../StyledElements'

const LINE_TYPES = {
  header: 'header',
  data: 'data',
  subtotal: 'subtotal',
}

const transformFinishedGoods = (finishedGoods, defaultYardUm) => {
  const header = {
    type: LINE_TYPES.header,
    name: 'Material Name',
    um: 'UM',
    pkgId: 'Pkg ID',
    pkgType: 'Pkg Type',
    gross: 'Gross',
    tare: 'Tare',
    net: 'Net',
  }

  const groupedFGs = groupFinishedGoodsByMaterial(finishedGoods)

  let totalGross = 0
  let totalTare = 0
  let totalNet = 0

  const formattedLineItems = Object.keys(groupedFGs).map(material => {
    const groupedByMaterialFinishedGood = groupedFGs[material]
    let subtotalGross = 0
    let subtotalTare = 0
    let subtotalNet = 0

    return groupedByMaterialFinishedGood.reduce((acc, finishedGood, index) => {
      totalGross += parseInt(finishedGood?.gross, 10) || 0
      totalTare += parseInt(finishedGood?.tare, 10) || 0
      totalNet += parseInt(finishedGood?.net, 10) || 0
      subtotalGross += parseInt(finishedGood?.gross, 10) || 0
      subtotalTare += parseInt(finishedGood?.tare, 10) || 0
      subtotalNet += parseInt(finishedGood?.net, 10) || 0

      const subtotalData = {
        type: 'subtotal',
        name: null,
        um: null,
        pkgId: null,
        pkgType: 'Total Material Weight',
        gross: subtotalGross,
        tare: subtotalTare,
        net: subtotalNet,
      }

      const fgData = {
        type: LINE_TYPES.data,
        //  we only show the material name and um on the first line item
        name: index === 0 ? finishedGood?.materialName : null,
        um: index === 0 ? defaultYardUm : null,
        pkgId: finishedGood?.fgId,
        pkgType: finishedGood?.type || finishedGood?.fgType,
        gross: finishedGood?.gross,
        tare: finishedGood?.tare,
        net: finishedGood?.net,
      }

      return index < groupedByMaterialFinishedGood.length - 1 ? [...acc, fgData] : [...acc, fgData, subtotalData]
    }, [])
  })

  const totals = {
    type: 'subtotal',
    name: null,
    um: null,
    pkgId: null,
    pkgType: 'Total Weight',
    gross: totalGross,
    tare: totalTare,
    net: totalNet,
  }

  return [header, ...formattedLineItems.flat(), totals]
}

const EmptyLine = () => <Divider style={{ padding: '16px 0px 0px 0px', margin: '0px 16px 0px 0px', flex: 1 }} />

const FORM_TYPES = {
  packingList: 'packingList',
  billOfLading: 'billOfLading',
}

const PrintablePackingListOrBoL = ({
  customer = {
    name: '',
  },
  overall,
  finishedGoods = [],
  notes = '',
  type = FORM_TYPES.packingList,
  shippingInfo,
  tag,
  millScaleNumber,
  millDeliveryNumber,
  bookingRef,
  details = [],
  looseMaterials,
  freightDestinationCustomerName,
}) => {
  const { useMinimalObt, enableCountryCodes } = useSelector(state => state.featureFlags?.variables || {})
  const theme = useTheme()
  const getBottomBorder = field => `${field ? 1 : 0}px solid ${theme.palette.borderColor.default}`
  const { addressName, address, address2, city, state, zip, country } = useSelector(state => state.settings.details)
  const showCountry = enableCountryCodes && !!country && !!shippingInfo?.country && country !== shippingInfo?.country
  const { defaultYardUm } = useSelector(state => state.settings)

  const materials = transformFinishedGoods(finishedGoods, defaultYardUm)

  const isPackingList = type === FORM_TYPES.packingList

  const printDate = new Date()
  const mats =
    looseMaterials?.map(material => ({
      gross: material.yardGross,
      tare: material.yardTare,
      net: material.yardNet,
      materialName: material?.code?.materialName,
    })) || []
  const { rows: materialsRows, totals } = aggregateFGsAndMaterials(finishedGoods, mats)
  const { gross, tare, net } = overall
  const netAmount = net?.amount || gross?.amount - tare?.amount
  const siteLocation = details.find(detail => detail.field === 'Site Location')?.value

  return (
    <>
      <Grid container>
        {shippingInfo && details.length > 0 && (
          <>
            {!isPackingList && !useMinimalObt && (
              <Grid
                container
                justifyContent="space-between"
                sx={{ border: '1px solid black', borderBottom: '0px', padding: 1 }}
              >
                <Grid item>
                  <Caption sx={{ fontSize: '14px' }}>Print Date</Caption>
                  <Typography variant="body2">{dateFormatter(printDate)}</Typography>
                </Grid>

                <Grid item mr={2}>
                  <Caption sx={{ fontSize: '14px' }}>{useMinimalObt ? 'WeighMaster' : 'Mill Scale Ticket #'}</Caption>
                  <Typography variant="body2">{millScaleNumber}</Typography>
                </Grid>

                <Grid item mr={2}>
                  <Caption sx={{ fontSize: '14px' }}>Mill Delivery #</Caption>
                  <Typography variant="body2">{millDeliveryNumber}</Typography>
                </Grid>

                <Grid item mr={2}>
                  <Caption sx={{ fontSize: '14px' }}>Bill of Lading #</Caption>
                  <Typography variant="body2">{tag}</Typography>
                </Grid>

                {useMinimalObt ? null : (
                  <Grid item mr={2}>
                    <Caption sx={{ fontSize: '14px' }}>Booking Ref #</Caption>
                    <Typography variant="body2">{bookingRef}</Typography>
                  </Grid>
                )}
                {useMinimalObt ? null : (
                  <Grid item>
                    <Grid container direction="row" justifyContent="flex-start" alignItems="flex-end">
                      <Grid item>
                        <Grid container direction="column" justifyContent="flex-start" alignItems="flex-end">
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" mr={2}>
                              Gross Carrier:
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" mr={2}>
                              Tare Carrier:
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" mr={2}>
                              Net Carrier:
                            </Typography>
                          </Stack>
                        </Grid>
                      </Grid>
                      <Grid item>
                        <Grid container direction="column" justifyContent="flex-start" alignItems="flex-end">
                          <Typography variant="body2">{`${formatNumber(gross?.amount || 0)} ${defaultYardUm}`}</Typography>
                        </Grid>
                        <Grid container direction="column" justifyContent="flex-start" alignItems="flex-end">
                          <Typography variant="body2">{`${formatNumber(tare?.amount || 0)} ${defaultYardUm}`}</Typography>
                        </Grid>
                        <Grid container direction="column" justifyContent="flex-start" alignItems="flex-end">
                          <Typography variant="body2">{`${formatNumber(netAmount || 0)} ${defaultYardUm}`}</Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            )}
            {/* Shipping Info */}
            <Grid item sx={{ border: '1px solid black', padding: 1 }} xs={useMinimalObt ? 12 : 3}>
              <Grid item>
                <strong>Ship From</strong>
                <Typography variant="body2">{addressName || ''}</Typography>
                <Typography variant="body2">{formatAddress({ address, address2 })}</Typography>
                <Typography variant="body2">{convertShippingInfoToCityStateZip({ city, state, zip })}</Typography>
                {showCountry && <Typography variant="body2">{countryCodeToName(country)}</Typography>}
              </Grid>
              {useMinimalObt ? <Divider style={{ margin: '16px -8px', borderColor: 'black' }} /> : null}
              <Grid item mt={2}>
                <strong>Ship To</strong>
                <Typography variant="body2">{freightDestinationCustomerName || customer?.name}</Typography>
                <Typography variant="body2">{shippingInfo?.address}</Typography>
                <Typography variant="body2">{convertShippingInfoToCityStateZip(shippingInfo)}</Typography>
                {showCountry && <Typography variant="body2">{countryCodeToName(shippingInfo.country)}</Typography>}
              </Grid>
            </Grid>

            {/* Details */}
            <Grid
              sx={{
                flex: 1,
                border: '1px solid black',
                ...(useMinimalObt ? { borderTop: '0px' } : { borderLeft: '0px' }),
                padding: 1,
              }}
              pl={2}
            >
              <strong>Details</strong>
              <Grid item container>
                {details
                  .filter(detail => {
                    if (!useMinimalObt) return true
                    return detail.field !== 'Site Location'
                  })
                  .map(detail => (
                    <Grid item xs={4} sm={3} mb={2} key={detail.field}>
                      <Caption sx={{ fontSize: '12px' }}>{detail.field}</Caption>
                      <Typography variant="body2">{detail.value}</Typography>
                      {detail.value2 ? <Typography variant="body2">{detail.value2}</Typography> : null}
                    </Grid>
                  ))}
                {notes && (
                  <Grid item xs={9}>
                    <Caption>Notes</Caption>
                    {notes.map(note => (
                      <Typography key={Base64.encode(note?.name)} variant="body2">
                        {note?.name}
                      </Typography>
                    ))}
                  </Grid>
                )}
              </Grid>
            </Grid>
          </>
        )}
      </Grid>

      <Grid sx={{ flex: 1, border: '1px solid black', borderTop: '0px', padding: 1 }} container>
        <Grid item xs={12}>
          <strong>{isPackingList ? 'Summary' : 'Order Information'}</strong>
        </Grid>

        <MaterialsSummaryTable rows={materialsRows} totals={totals} isPackingList={isPackingList} />
      </Grid>

      {!isPackingList && useMinimalObt && (
        <>
          <Grid container sx={{ border: '1px solid black', borderTop: '0px', padding: 1 }}>
            <Grid item xs={12}>
              <strong>Location</strong>
              <Typography variant="body2">{siteLocation}</Typography>
            </Grid>
          </Grid>
          <Grid container sx={{ border: '1px solid black', borderTop: '0px', padding: 1 }}>
            <Grid item xs={12}>
              <strong>WeighMaster</strong>
              <Typography variant="body2">{millScaleNumber}</Typography>
            </Grid>
          </Grid>
        </>
      )}

      {isPackingList && (
        <Grid container sx={{ flex: 1, border: '1px solid black', borderTop: '0px', padding: 1 }}>
          <Grid item xs={12}>
            <strong>Finished Goods</strong>
          </Grid>
          {materials.map(({ type, ...rest }, index) => (
            <Grid
              key={`material-${index}`}
              sx={{
                marginTop: index === 0 ? 2 : 1,
                marginBottom: type === 'subtotal' ? 1 : 0,
                paddingLeft: 1,
              }}
              container
              item
              xs={12}
            >
              {Object.keys(rest).map(field => (
                <Grid
                  key={`material-${index}-${field}`}
                  xs={field === 'name' ? 4 : field === 'pkgType' ? 3 : 1}
                  sx={{
                    borderBottom: getBottomBorder(rest[field]),
                    paddingLeft: field === 'pkgType' ? 2 : 0,
                    textAlign: field === 'gross' || field === 'tare' || field === 'net' ? 'right' : 'left',
                  }}
                  item
                >
                  <Typography
                    variant="body1"
                    fontWeight={type === 'header' || type === 'subtotal' ? 'bold' : 'regular'}
                  >
                    {field === 'gross' || field === 'tare' || field === 'net' ? formatNumber(rest[field]) : rest[field]}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          ))}
        </Grid>
      )}

      {!isPackingList && (
        <>
          <Grid container>
            {useMinimalObt ? null : (
              <Grid item xs={6} sx={{ border: '1px solid black', borderTop: '0px', borderRight: '0px', padding: 1 }}>
                <strong>Freight Terms</strong>

                <GridSpacer container xs={12} mt={0.5} mb={0.5}>
                  <Grid item xs={6}>
                    <Caption>FREIGHT PAYER</Caption>
                    <EmptyLine />
                  </Grid>
                  <Grid item xs={6}>
                    <Caption>CHARGE TERMS</Caption>
                    <EmptyLine />
                  </Grid>
                </GridSpacer>

                <Typography fontSize=".5rem">
                  The carrier shall not make delivery of this shipment without payment of freight and all other lawful
                  charges.
                </Typography>

                <SignatureDateLine />
              </Grid>
            )}

            <Grid
              item
              xs={useMinimalObt ? 12 : 6}
              py={1}
              pl={2}
              sx={{ border: '1px solid black', borderTop: '0px', padding: 1 }}
            >
              <strong>Disclaimer</strong>

              <Typography variant="body2" fontSize={useMinimalObt ? '.6rem' : '.4rem'}>
                {useMinimalObt
                  ? `
                  This is to certify that the following described commodity was weighed,
                  measured or counted by a WeighMaster whose signature is on this certificate,
                  who is a recognized authority of accuracy, as prescribed by NIST handbook 44
                  and the Alabama Department of Weighs & Measures, Article 3, Title 3, Code of Alabama 1975.
                `
                  : `
                  RECEIVED, subject to the rates, classifications and rules that
                  have been established by the Carrier and are available on
                  request to the Shipper (Shipper defined in 49 U.S.C.A. §
                  13102(13)(c)), and to all applicable state and federal
                  regulations. Shipper 1) warrants it has read all applicable
                  contract(s) or Carrier's applicable tariff(s) and the limitation
                  of liability provisions set forth therein; and 2) has actual
                  knowledge of and accepts the applicable contract or tariff
                  terms, including the limits on carrier liability. Carriers'
                  tariff(s), including OD Rules 100, take precedence in the event
                  of any terms or conditions conflicts
                `}
              </Typography>
            </Grid>
          </Grid>

          {useMinimalObt ? null : (
            <Grid container>
              <Grid item xs={4} sx={{ border: '1px solid black', borderTop: '0px', padding: 1 }}>
                <strong>Shipper Signature</strong>

                <Typography variant="body2" fontSize=".4rem">
                  This is to certify that the above named materials are properly classified, described, packaged, marked
                  and labeled, and are in proper condition for transportation according to the applicable regulations of
                  the U.S. DOT{' '}
                </Typography>

                <SignatureDateLine />
              </Grid>

              <Grid item xs={4} sx={{ borderBottom: '1px solid black', padding: 1 }}>
                <Grid container xs={12}>
                  <Grid item xs={12}>
                    <div>
                      <Caption>TRAILER LOADED</Caption>
                    </div>
                    <FormControlLabel
                      control={<Checkbox />}
                      label={<Typography fontSize=".75rem">By Shipper</Typography>}
                    />
                    <FormControlLabel
                      control={<Checkbox />}
                      label={<Typography fontSize=".75rem">By Driver</Typography>}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <div>
                      <Caption>FREIGHT COUNTED</Caption>
                    </div>
                    <FormControlLabel
                      control={<Checkbox />}
                      label={<Typography fontSize=".75rem">By Shipper</Typography>}
                    />
                    <FormControlLabel
                      control={<Checkbox />}
                      label={<Typography fontSize=".75rem">By Driver</Typography>}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={4} sx={{ border: '1px solid black', borderTop: '0px', padding: 1 }}>
                <Grid item>
                  <strong>Carrier Signature</strong>

                  <Typography variant="body2" fontSize=".4rem">
                    Carrier acknowledges receipt of packages and required placards. Carrier certifies emergency response
                    information was made available and/or carrier has the U.S. DOT emergency response guidebook or
                    equivalent documentation in the vehicle. Property described above is received in good order, except
                    as noted.
                  </Typography>

                  <SignatureDateLine />
                </Grid>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </>
  )
}

export default PrintablePackingListOrBoL
